import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../components/Icon';
import useToast from '../../components/toast/useToast';
import { useHighlight } from '../../hooks/useHighlight';
import TransportChip from '../beam/TransportChip';
import { asGistMarkdown } from './diff';
import './CodeBubble.css';

// Hard cap on rendered lines. Anything beyond this collapses to a "too large"
// fallback so a 50k-line paste can't lock the renderer. Open question in the
// F-doc resolved to "render fallback" rather than route through FileCard
// (FileCard isn't in this wave).
const HARD_LINE_CAP = 5000;

// Default collapse threshold — when `defaultCollapsed` is set and the snippet
// has more lines than this, we render the first N + a "Show all" link.
const COLLAPSE_THRESHOLD = 20;

// Copy-button "check" flash duration.
const COPY_FLASH_MS = 1000;

/**
 * CodeBubble — pure view component for an inbound code snippet (F12.1).
 *
 * Markup mirrors `design/sharing-screens.jsx::CodeSnippet`:
 *   .code-snip
 *     .cs-head : lang label + filename + copy button
 *     <pre>    : one `.ln` span per line, with a CSS-counter gutter for
 *                line numbers (see CodeBubble.css)
 *     .cs-foot : transport chip + total-line count
 *
 * Highlight tokens come from `useHighlight(text, lang)` — the hook returns
 * plain spans immediately and swaps in token-classed spans once the grammar
 * chunk has loaded. That keeps first paint synchronous.
 *
 * Props:
 *   - `lang`             string — e.g. 'ts' / 'js' / 'py'. Rendered uppercased.
 *   - `title`            ?string — filename, e.g. 'beam.ts'.
 *   - `text`             string — RAW source. Clipboard writes this; the
 *                        highlighter receives this; line numbers count this.
 *   - `via`              'lan' | 'cloud' — transport snapshot for the chip.
 *   - `wide`             ?boolean — drops the bubble's maxWidth (used by the
 *                        composer modal's preview in F12.2).
 *   - `defaultCollapsed` ?boolean — when true AND the snippet has more than
 *                        20 lines, render the first 20 with a "Show all (N
 *                        lines)" link below.
 *   - `onCompareRequest` ?(snippet) => void — when supplied, renders the
 *                        F12.4 "Compare with…" action in the footer. The
 *                        caller (usually MessageList) opens a picker of
 *                        matching-lang snippets and a DiffView. When omitted
 *                        the action is hidden — feature degrades gracefully
 *                        in standalone surfaces like the composer preview.
 */
function CodeBubble({
  lang = '',
  title,
  text = '',
  via = 'cloud',
  wide = false,
  defaultCollapsed = false,
  onCompareRequest,
}) {
  const { showToast } = useToast();
  const lines = useHighlight(text, lang);
  const totalLines = lines.length;
  const overCap = totalLines > HARD_LINE_CAP;

  // Local copy-button "just copied" state, drives the icon swap + label.
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef(null);
  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  // Collapse state — local, not persisted. Auto-collapse only when the
  // caller opts in AND the snippet is long enough to warrant it.
  const canCollapse = defaultCollapsed && totalLines > COLLAPSE_THRESHOLD;
  const [expanded, setExpanded] = useState(!canCollapse);

  // Reset expansion when the collapse-eligibility changes (e.g. text shrinks).
  useEffect(() => {
    if (!canCollapse) setExpanded(true);
  }, [canCollapse]);

  const visibleLines = useMemo(() => {
    if (overCap) return [];
    if (canCollapse && !expanded) return lines.slice(0, COLLAPSE_THRESHOLD);
    return lines;
  }, [lines, canCollapse, expanded, overCap]);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      showToast('Code copied');
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => {
        setCopied(false);
        copyTimerRef.current = null;
      }, COPY_FLASH_MS);
    } catch {
      // Clipboard write rejected (permissions, no secure context). Surface a
      // toast and leave the icon unchanged — no half-success states.
      showToast("Couldn't copy");
    }
  }, [text, showToast]);

  const handleExpand = useCallback(() => setExpanded(true), []);

  // F12.4 — Copy as Markdown (Gist-style fenced block).
  const handleCopyGist = useCallback(async () => {
    try {
      const md = asGistMarkdown({ lang, text });
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(md);
      }
      showToast('Copied as Markdown');
    } catch {
      showToast("Couldn't copy");
    }
  }, [lang, text, showToast]);

  // F12.4 — Compare with… A no-op if the caller didn't wire the picker.
  const handleCompare = useCallback(() => {
    if (typeof onCompareRequest === 'function') {
      onCompareRequest({ lang, title, text });
    }
  }, [lang, title, text, onCompareRequest]);

  // Too-large fallback. Skips the highlight pipeline entirely on render.
  if (overCap) {
    return (
      <div className={`code-snip${wide ? ' code-snip--wide' : ''}`} data-testid="code-bubble">
        <div className="cs-head">
          <Icon name="code" size={14} stroke="#8fdcff" />
          <span className="cs-lang">{String(lang || '').toUpperCase()}</span>
          {title && <span className="cs-title">{title}</span>}
        </div>
        <div className="cs-toolarge">
          <span>
            Snippet too large to display ({totalLines} lines) — open as file
          </span>
          <button type="button" disabled>
            Open
          </button>
        </div>
        <div className="cs-foot">
          <TransportChip
            via={via}
            label={via === 'lan' ? 'shared over LAN' : 'synced via Cloud'}
            size="sm"
          />
          <span style={{ marginLeft: 'auto' }}>{totalLines} lines</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`code-snip${wide ? ' code-snip--wide' : ''}`} data-testid="code-bubble">
      <div className="cs-head">
        <Icon name="code" size={14} stroke="#8fdcff" />
        <span className="cs-lang">{String(lang || '').toUpperCase()}</span>
        {title && <span className="cs-title">{title}</span>}
        <button
          type="button"
          className={`cs-copy${copied ? ' cs-copy--copied' : ''}`}
          onClick={handleCopy}
          aria-label={copied ? 'Code copied' : 'Copy code'}
        >
          <Icon name={copied ? 'check' : 'copy'} size={13} stroke="currentColor" />
        </button>
      </div>
      <pre>
        {visibleLines.map((line, i) => (
          <span className="ln" key={i}>
            {line}
            {'\n'}
          </span>
        ))}
      </pre>
      {canCollapse && !expanded && (
        <div className="cs-foot">
          <button type="button" className="cs-expand" onClick={handleExpand}>
            Show all ({totalLines} lines)
          </button>
        </div>
      )}
      <div className="cs-foot">
        <TransportChip
          via={via}
          label={via === 'lan' ? 'shared over LAN' : 'synced via Cloud'}
          size="sm"
        />
        <span className="cs-actions" role="group" aria-label="Code snippet actions">
          {typeof onCompareRequest === 'function' && (
            <button
              type="button"
              className="cs-action"
              onClick={handleCompare}
              aria-label="Compare with another snippet"
            >
              Compare
            </button>
          )}
          <button
            type="button"
            className="cs-action"
            onClick={handleCopyGist}
            aria-label="Copy as Markdown code block"
          >
            Copy as MD
          </button>
        </span>
        <span className="cs-lines">{totalLines} lines</span>
      </div>
    </div>
  );
}

export default CodeBubble;
export { HARD_LINE_CAP as __HARD_LINE_CAP, COLLAPSE_THRESHOLD as __COLLAPSE_THRESHOLD };
